import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

// ============================================================================
// Types
// ============================================================================

type Theme = 'light' | 'dark';

/** Maps numeric role IDs to [data-role] attribute values used in variables.css */
export type RoleTheme = 'admin' | 'org' | 'audit' | 'risk' | 'csuite' | 'costing' | '';

/** Maps from numeric role constant → data-role attribute value */
export const ROLE_THEME_MAP: Record<number, RoleTheme> = {
  1: 'admin',
  2: 'org',
  3: 'audit',
  4: 'risk',
  5: 'csuite',
  6: 'costing',
};

/** Loads only the CSS chunk for the active role — all others are never fetched */
const ROLE_CSS_LOADERS: Partial<Record<RoleTheme, () => Promise<unknown>>> = {
  admin:   () => import('../styles/app1.css'),
  csuite:  () => import('../styles/app2.css'),
  org:     () => import('../styles/app3.css'),
  risk:    () => import('../styles/app4.css'),
  audit:   () => import('../styles/app5.css'),
  costing: () => import('../styles/app6.css'),
};

interface ThemeContextValue {
  theme: Theme;
  roleTheme: RoleTheme;
  toggleTheme: () => void;
  setTheme: (t: Theme) => void;
  /** Call this after login with the numeric role id (e.g. 1, 2, 3 …) */
  setRole: (roleId: number | null) => void;
}

// ============================================================================
// Context
// ============================================================================

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY_THEME = 'anygrc_theme';
const STORAGE_KEY_ROLE  = 'anygrc_role_theme';

// ============================================================================
// Provider
// ============================================================================

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // ── Light / Dark mode ────────────────────────────────────────────────────
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = localStorage.getItem(STORAGE_KEY_THEME) as Theme | null;
    return stored === 'dark' || stored === 'light' ? stored : 'light';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(STORAGE_KEY_THEME, theme);
  }, [theme]);

  const setTheme   = useCallback((t: Theme) => setThemeState(t), []);
  const toggleTheme = useCallback(
    () => setThemeState(prev => (prev === 'light' ? 'dark' : 'light')),
    []
  );

  // ── Role theme ───────────────────────────────────────────────────────────
  const isSuperAdminPath = () => window.location.pathname.startsWith('/super-admin');
  const isLoginPath = () => {
    const p = window.location.pathname;
    return p === '/login' || p === '/';
  };

  const [roleTheme, setRoleThemeState] = useState<RoleTheme>(() => {
    if (isSuperAdminPath() || isLoginPath()) return '';
    const stored = localStorage.getItem(STORAGE_KEY_ROLE) as RoleTheme | null;
    return stored ?? '';
  });

  // Track super-admin state reactively so URL changes (popstate/pushstate) are caught.
  const [isSuperAdmin, setIsSuperAdmin] = useState(isSuperAdminPath);
  const prevPathRef = useRef(window.location.pathname);

  useEffect(() => {
    const checkPath = () => {
      const next = isSuperAdminPath();
      if (next !== (prevPathRef.current.startsWith('/super-admin'))) {
        setIsSuperAdmin(next);
      }
      // When navigating to login, always strip the role theme.
      if (isLoginPath()) {
        setRoleThemeState('');
        localStorage.removeItem(STORAGE_KEY_ROLE);
        document.documentElement.removeAttribute('data-role');
      }
      prevPathRef.current = window.location.pathname;
    };
    window.addEventListener('popstate', checkPath);
    // Patch pushState / replaceState so client-side navigation is caught too.
    const origPush    = window.history.pushState.bind(window.history);
    const origReplace = window.history.replaceState.bind(window.history);
    window.history.pushState    = (...a) => { origPush(...a);    checkPath(); };
    window.history.replaceState = (...a) => { origReplace(...a); checkPath(); };
    return () => {
      window.removeEventListener('popstate', checkPath);
      window.history.pushState    = origPush;
      window.history.replaceState = origReplace;
    };
  }, []);

  /** Writes [data-role] to <html> and lazy-loads only that app's CSS chunk */
  useEffect(() => {
    if (isSuperAdmin) {
      // `:root` defaults are violet — pin super-admin immediately so there is
      // never a frame where the wrong brand color shows.
      document.documentElement.setAttribute('data-role', 'super-admin');
      return;
    }
    if (roleTheme) {
      document.documentElement.setAttribute('data-role', roleTheme);
      ROLE_CSS_LOADERS[roleTheme]?.();
      localStorage.setItem(STORAGE_KEY_ROLE, roleTheme);
    } else {
      document.documentElement.removeAttribute('data-role');
      localStorage.removeItem(STORAGE_KEY_ROLE);
    }
  }, [roleTheme, isSuperAdmin]);

  const setRole = useCallback((roleId: number | null) => {
    const mapped = roleId != null ? (ROLE_THEME_MAP[roleId] ?? '') : '';
    setRoleThemeState(mapped);
  }, []);

  // ── Provider ─────────────────────────────────────────────────────────────
  return (
    <ThemeContext.Provider value={{ theme, roleTheme, toggleTheme, setTheme, setRole }}>
      {children}
    </ThemeContext.Provider>
  );
};

// ============================================================================
// Hook
// ============================================================================

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
};
